import React, { createContext, useContext, useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';

const SERVER_URL = process.env.NODE_ENV === 'production' ? window.location.origin : (process.env.REACT_APP_SERVER_URL || 'http://localhost:4000');
const SESSION_TOKEN_KEY = 'who-pays-session-token';

function getOrCreateSessionToken(): string {
  let token = localStorage.getItem(SESSION_TOKEN_KEY);
  if (!token) {
    token = crypto.randomUUID();
    localStorage.setItem(SESSION_TOKEN_KEY, token);
  }
  return token;
}

interface SocketContextValue {
  socket: Socket | null;
  socketId: string | null;
  sessionToken: string;
  isConnected: boolean;
}

const SocketContext = createContext<SocketContextValue>({
  socket: null,
  socketId: null,
  sessionToken: '',
  isConnected: false,
});

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [socketId, setSocketId] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [sessionToken] = useState(getOrCreateSessionToken);

  useEffect(() => {
    const newSocket = io(SERVER_URL, { autoConnect: true });
    setSocket(newSocket);

    newSocket.on('connect', () => {
      setIsConnected(true);
      setSocketId(newSocket.id ?? null);
    });
    newSocket.on('disconnect', () => {
      setIsConnected(false);
      setSocketId(null);
    });

    return () => {
      newSocket.disconnect();
    };
  }, []);

  return (
    <SocketContext.Provider value={{ socket, socketId, sessionToken, isConnected }}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => useContext(SocketContext);
